// Report Draft 탭 — 바닐라 dcf.html #tab-report / dcf.js 의
// renderPptPresets·selectPptPreset·togglePptCustomTheme(PPT 테마 프리셋+커스텀
// 색상/폰트), onPptLogoFile·renderLogoPreview(로고 문구/이미지 업로드·표지 배치
// 미리보기), genFile(산출물 다운로드 — POST /agent/dcf/excel → d.url,
// POST /agent/dcf/ppt → d.ppt_url/d.pdf_url, #file-links 패턴),
// generateReportDraft(보고서 초안 — POST /agent/llm + PERSONA),
// renderReportSummary(평가결과 요약) 이식.
// 테마 payload 계산·표지 배치 계산은 lib/pptTheme.ts (순수 함수, 단위테스트).
import { useState } from "react";
import { api } from "../../lib/api";
import { triggerDownload } from "../../lib/download";
import { fmt, pct } from "../../lib/format";
import { Button } from "../../ui/Button";
import { unitMoney, unitShare, useT } from "../../lib/i18n";
import {
  buildPptTheme,
  computeLogoPlacement,
  LOGO_PREVIEW_PX_PER_IN,
  PPT_FONTS,
  PPT_PRESETS,
} from "../../lib/pptTheme";
import { useDcf, type DcfFileLink } from "../../stores/dcf";

interface OkResp {
  ok: boolean;
  message?: string;
}

// 바닐라 dcf.js PERSONA — 보고서·검토용 전문가 페르소나 (동일 문구 유지)
// SVG → PNG 래스터화 (바닐라 svgToPngDataUrl) — python-pptx 는 SVG 를 직접
// 삽입할 수 없어 브라우저 캔버스로 변환. AI/EPS/PDF 등 다른 벡터 포맷은
// 브라우저가 디코딩할 수 없어 지원 범위를 SVG 까지로 한정한다 (바닐라 동일).
function svgToPngDataUrl(svgDataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const natW = img.naturalWidth;
      const natH = img.naturalHeight;
      if (!natW || !natH) {
        reject(new Error("SVG 크기를 확인할 수 없습니다"));
        return;
      }
      const targetH = 400;
      const targetW = Math.max(1, Math.round(targetH * (natW / natH)));
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("canvas 를 사용할 수 없습니다"));
        return;
      }
      ctx.drawImage(img, 0, 0, targetW, targetH);
      try {
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => reject(new Error("SVG 를 열 수 없습니다"));
    img.src = svgDataUrl;
  });
}

/** 로고 미리보기 상태 — 바닐라 #ppt-logo-preview */
interface LogoPreview {
  src: string;
  wPx: number;
  hPx: number;
  note: string;
}

// 바닐라 FILE_META — kind → 엔드포인트/응답 URL 키
const FILE_META = {
  excel: { path: "/agent/dcf/excel", urlKey: "url", label: "Excel" },
  ppt: { path: "/agent/dcf/ppt", urlKey: "ppt_url", label: "PPT" },
  pdf: { path: "/agent/dcf/ppt", urlKey: "pdf_url", label: "PDF" },
} as const;
type FileKind = keyof typeof FILE_META;

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export default function ReportTab() {
  const { lang, t, tf } = useT();
  const current = useDcf((s) => s.current);
  const setBusy = useDcf((s) => s.setBusy);
  // 파일 목록은 store 공유 — 채팅 download artifact 도 같은 목록에 쌓인다
  const fileLinks = useDcf((s) => s.fileLinks);
  const setFileLinks = useDcf((s) => s.setFileLinks);
  // ── PPT 테마 상태 (바닐라 pptThemeState/pptLogoBase64) ──
  const [preset, setPreset] = useState<string>("navy_classic");
  const [customOn, setCustomOn] = useState(false);
  const [navy, setNavy] = useState("#1F3364");
  const [blueMid, setBlueMid] = useState("#4A82D4");
  const [font, setFont] = useState<string>(PPT_FONTS[0]);
  const [logoText, setLogoText] = useState("");
  const [logoB64, setLogoB64] = useState<string | null>(null);
  const [logoStatus, setLogoStatus] = useState("");
  const [logoPreview, setLogoPreview] = useState<LogoPreview | null>(null);
  if (!current) return null;
  const p = current.payload;
  const r = current.result;

  // 바닐라 selectPptPreset — 프리셋 선택 시 커스텀 체크 해제
  const selectPreset = (key: string) => {
    setPreset(key);
    setCustomOn(false);
  };

  // 바닐라 togglePptCustomTheme — 켤 때 현재 프리셋 색상으로 초기화
  const toggleCustom = (on: boolean) => {
    setCustomOn(on);
    if (on) {
      const pr = PPT_PRESETS.find((x) => x.key === preset) || PPT_PRESETS[0];
      setNavy(pr.navy);
      setBlueMid(pr.blueMid);
    }
  };

  // 바닐라 renderLogoPreview — 표지 배치 규칙(dcf_ppt.py)과 동일 계산으로 미리보기
  const showLogoPreview = (dataUrl: string, natW: number, natH: number) => {
    const pl = computeLogoPlacement(natW, natH);
    if (!pl) {
      setLogoPreview(null);
      return;
    }
    setLogoPreview({
      src: dataUrl,
      wPx: Math.round(pl.w * LOGO_PREVIEW_PX_PER_IN),
      hPx: Math.round(pl.h * LOGO_PREVIEW_PX_PER_IN),
      note: pl.capped
        ? `가로가 길어 너비 기준으로 맞춰집니다 (약 ${pl.w.toFixed(2)}in × ${pl.h.toFixed(2)}in — 원본 ${natW}×${natH})`
        : `높이 기준으로 배치됩니다 (약 ${pl.w.toFixed(2)}in × ${pl.h.toFixed(2)}in — 원본 ${natW}×${natH})`,
    });
  };

  // 바닐라 _finalizeLogoImage — 실제 디코딩 가능 여부를 확인한 뒤 적용
  const finalizeLogo = (dataUrl: string, fileName: string, input: HTMLInputElement) => {
    const testImg = new Image();
    testImg.onload = () => {
      setLogoB64(dataUrl);
      setLogoStatus(`✓ ${fileName} 적용됨`);
      showLogoPreview(dataUrl, testImg.naturalWidth, testImg.naturalHeight);
      // 바닐라 동일 — 로고 업로드 시 커스텀 박스도 함께 연다
      if (!customOn) toggleCustom(true);
    };
    testImg.onerror = () => {
      setLogoB64(null);
      setLogoStatus(
        "이미지를 열 수 없습니다 — PNG 또는 JPG 파일로 변환한 뒤 다시 업로드해주세요.",
      );
      input.value = "";
      setLogoPreview(null);
    };
    testImg.src = dataUrl;
  };

  // 바닐라 onPptLogoFile — 2MB 제한, SVG 는 캔버스로 PNG 변환
  const onLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) {
      setLogoB64(null);
      setLogoStatus("");
      setLogoPreview(null);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoStatus("이미지가 너무 큽니다 (2MB 이하).");
      input.value = "";
      setLogoB64(null);
      setLogoPreview(null);
      return;
    }
    const isSvg = file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (isSvg) {
        setLogoStatus("SVG 변환 중…");
        svgToPngDataUrl(dataUrl)
          .then((png) => finalizeLogo(png, file.name, input))
          .catch(() => {
            setLogoB64(null);
            setLogoStatus(
              "SVG를 변환하지 못했습니다 — 디자인 툴에서 PNG로 내보내 다시 업로드해주세요.",
            );
            input.value = "";
            setLogoPreview(null);
          });
        return;
      }
      // HEIC/WebP/손상 파일 등은 여기서 걸러짐 (서버에 보내기 전 즉시 안내)
      finalizeLogo(dataUrl, file.name, input);
    };
    reader.readAsDataURL(file);
  };

  // ── 산출물 생성 (바닐라 genFile) ──
  const genFile = async (kind: FileKind) => {
    const meta = FILE_META[kind];
    setFileLinks([]);
    setBusy(meta.label + " 파일을 생성 중입니다...");
    try {
      let payload = current.payload;
      if (kind === "ppt" || kind === "pdf") {
        // 바닐라 buildPptThemePayload — 기본 프리셋 + 로고/커스텀 없음이면 테마 미전송
        const theme = buildPptTheme({
          preset,
          custom: customOn,
          navy,
          blueMid,
          font,
          logoText,
          logoImageBase64: logoB64,
        });
        if (theme) payload = { ...payload, ppt_theme: theme };
      }
      const d = await api<OkResp & Record<string, unknown>>(meta.path, {
        method: "POST",
        body: JSON.stringify({ payload }),
      });
      if (!d.ok) throw new Error((d.message as string) || "백엔드 오류");
      const url = d[meta.urlKey] as string | undefined;
      const next: DcfFileLink[] = [];
      if (d.logo_warning)
        next.push({ id: Date.now(), kind: "warn", label: String(d.logo_warning) });
      if (url) {
        triggerDownload(url);
        next.push({ id: Date.now() + 1, kind: "ok", label: meta.label, url });
      } else {
        next.push({
          id: Date.now() + 1,
          kind: "err",
          label: `${meta.label}는 현재 제공되지 않습니다.`,
        });
      }
      setFileLinks(next);
    } catch (e) {
      setFileLinks([
        { id: Date.now(), kind: "err", label: `${meta.label} 생성 실패: ${errMsg(e)}` },
      ]);
    } finally {
      setBusy(null);
    }
  };

  // ── 평가결과 요약 (바닐라 renderReportSummary) ──
  const b = r?.bridge;
  const rpv = r?.pv || {};
  const tvRatio =
    b && rpv.pv_terminal && b.operating_value
      ? rpv.pv_terminal / b.operating_value
      : null;
  const m = unitMoney(lang);
  const summaryRows: Array<[string, string]> | null = b
    ? [
        [t("lbl.company"), p.target_name || "—"],
        [t("lbl.val_date"), p.valuation_date || "—"],
        [t("lbl.method"), "DCF (FCFF)"],
        [t("lbl.forecast_period"), (p.forecast_full_years || 5) + t("unit.years_suffix")],
        [t("lbl.wacc_row"), pct(p.discount_rate)],
        [t("lbl.tgr_row"), pct(p.terminal_growth_rate)],
        [t("lbl.tax_row"), pct(p.tax_rate)],
        ["Operating Value", fmt(b.operating_value) + m],
        [t("br.non_op"), fmt(b.non_operating_assets) + m],
        [t("br.debt"), fmt(b.interest_bearing_debt) + m],
        ["Equity Value", fmt(b.equity_value) + m],
        [
          t("lbl.diluted_row"),
          fmt(b.diluted_shares_outstanding || b.shares_outstanding) +
            t("unit.shares_suffix"),
        ],
        [
          t("col.pps"),
          fmt(b.diluted_price_per_share || b.price_per_share) + unitShare(lang),
        ],
        [t("kpi.tv_sub"), pct(tvRatio)],
      ]
    : null;

  return (
    <>
      <div className="data-card">
        <h4>
          {t("h.ppt_theme")} <span className="hint">{t("hint.ppt_theme")}</span>
        </h4>
        <div className="ppt-preset-row">
          {PPT_PRESETS.map((x) => (
            <button
              key={x.key}
              type="button"
              className={`ppt-preset-chip${!customOn && preset === x.key ? " active" : ""}`}
              onClick={() => selectPreset(x.key)}
            >
              <span
                className="ppt-preset-dot"
                style={{
                  background: `linear-gradient(135deg, ${x.navy} 50%, ${x.blueMid} 50%)`,
                }}
              />
              {t(x.labelKey)}
            </button>
          ))}
        </div>
        {/* 로고 문구/이미지 — 커스텀 체크와 무관하게 항상 적용 (바닐라 동일) */}
        <div style={{ marginTop: 12 }}>
          <div className="wacc-row">
            <span className="wacc-label">{t("lbl.ppt_logo_text")}</span>
            <input
              className="wacc-input"
              style={{ width: 220 }}
              placeholder="DeepSearch"
              value={logoText}
              onChange={(e) => setLogoText(e.target.value)}
            />
          </div>
          <div className="wacc-row">
            <span className="wacc-label">
              {t("lbl.ppt_logo_img")} <span className="hint">{t("hint.ppt_logo_fmt")}</span>
            </span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={onLogoFile}
            />
          </div>
          {logoStatus && (
            <p className="hint" style={{ marginTop: 4 }}>
              {logoStatus}
            </p>
          )}
          <p className="hint" style={{ marginTop: 2 }}>
            {t("hint.ppt_logo_vector")}
          </p>
          {logoPreview && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                border: "1px solid var(--gray-3)",
                borderRadius: 6,
                background: "#fff",
              }}
            >
              <p className="hint" style={{ margin: "0 0 8px" }}>
                {t("lbl.ppt_logo_preview")}
              </p>
              <img
                src={logoPreview.src}
                alt=""
                style={{ display: "block", width: logoPreview.wPx, height: logoPreview.hPx }}
              />
              <p className="hint" style={{ margin: "8px 0 0" }}>
                {logoPreview.note}
              </p>
            </div>
          )}
          <p className="hint" style={{ marginTop: 2 }}>
            {t("hint.ppt_logo_always")}
          </p>
        </div>
        {/* 색상·폰트 커스터마이징 (바닐라 #ppt-theme-custom-toggle/-box) */}
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            marginTop: 12,
            fontSize: 12.5,
            color: "var(--text-secondary)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={customOn}
            onChange={(e) => toggleCustom(e.target.checked)}
          />
          <span>{t("lbl.ppt_custom")}</span>
        </label>
        {customOn && (
          <div style={{ marginTop: 10 }}>
            <div className="wacc-row">
              <span className="wacc-label">{t("lbl.ppt_navy")}</span>
              <input type="color" value={navy} onChange={(e) => setNavy(e.target.value)} />
            </div>
            <div className="wacc-row">
              <span className="wacc-label">{t("lbl.ppt_accent")}</span>
              <input
                type="color"
                value={blueMid}
                onChange={(e) => setBlueMid(e.target.value)}
              />
            </div>
            <div className="wacc-row">
              <span className="wacc-label">{t("lbl.ppt_font")}</span>
              <select
                className="wacc-input"
                style={{ width: 220 }}
                value={font}
                onChange={(e) => setFont(e.target.value)}
              >
                {PPT_FONTS.map((f) => (
                  <option key={f} value={f}>
                    {f === "Malgun Gothic" ? "Malgun Gothic (기본)" : f}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="data-card">
        <h4>{t("h.output_files")}</h4>
        <div className="report-actions">
          <Button variant="primary" onClick={() => void genFile("excel")}>
            {t("btn.gen_excel")}
          </Button>
          <Button variant="default" onClick={() => void genFile("ppt")}>
            {t("btn.gen_ppt")}
          </Button>
          <Button variant="default" onClick={() => void genFile("pdf")}>
            {t("btn.gen_pdf")}
          </Button>
        </div>
        <div>
          {fileLinks.map((en) =>
            en.kind === "ok" ? (
              <div
                key={en.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 13,
                  margin: "6px 0",
                }}
              >
                <span style={{ color: "var(--navy)", fontWeight: 600 }}>
                  {tf("msg.download_started", { label: en.label })}
                </span>
                <a
                  className="dl-link"
                  style={{ marginTop: 0, fontSize: 13 }}
                  href={en.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  {t("btn.download_again")}
                </a>
              </div>
            ) : (
              <div key={en.id}>
                <span
                  className="hint"
                  style={en.kind === "warn" ? { color: "#b45309" } : undefined}
                >
                  {en.kind === "warn" ? "⚠ " : ""}
                  {en.label}
                </span>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="data-card">
        <h4>{t("h.dcf_summary")}</h4>
        {summaryRows ? (
          <table className="data-table">
            <tbody>
              {summaryRows.map(([label, val], i) => (
                <tr key={i}>
                  <td>{label}</td>
                  <td>{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="hint">{t("ph.recalc_show")}</p>
        )}
      </div>
    </>
  );
}
